/**
 * DriverPage.js  –  Driver Dashboard
 * View all transport requests · Accept/Start/Complete trips · Trip history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';

const gold   = '#C9A84C';
const green  = '#2ecc71';
const red    = '#e74c3c';
const orange = '#f39c12';
const blue   = '#3498db';

const STATUS_COLOR = { pending: orange, confirmed: blue, completed: green, cancelled: '#666' };
const STATUS_ICON  = { pending: '⏳', confirmed: '🔵', completed: '✅', cancelled: '⛔' };
const VEHICLE_ICON = { saloon: '🚗', suv: '🚙', minivan: '🚐', shuttle: '🚌' };

export default function DriverPage({ notify }) {
  const [tab, setTab]       = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [detail, setDetail]     = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getTransportRequests();
      setRequests(r.data.results || r.data || []);
    } catch { notify?.('Failed to load transport requests', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const pending   = requests.filter(r => r.status === 'pending');
  const confirmed = requests.filter(r => r.status === 'confirmed');
  const completed = requests.filter(r => r.status === 'completed');
  const cancelled = requests.filter(r => r.status === 'cancelled');

  const todayStr = new Date().toISOString().split('T')[0];
  const todayTrips = requests.filter(r => r.pickup_datetime?.startsWith(todayStr));
  const todayRevenue = todayTrips.filter(t => t.status === 'completed').reduce((s, t) => s + parseFloat(t.fee || 0), 0);

  const doConfirm = async (id) => {
    setUpdating(id);
    try {
      await api.updateTransportRequest(id, { status: 'confirmed' });
      notify?.('Trip confirmed — ready to dispatch');
      load();
      if (detail?.id === id) setDetail(prev => ({ ...prev, status: 'confirmed' }));
    } catch { notify?.('Failed to confirm trip', 'error'); }
    finally { setUpdating(null); }
  };

  const doComplete = async (id) => {
    setUpdating(id);
    try {
      await api.completeTransport(id);
      notify?.('Trip marked as completed ✅');
      load();
      if (detail?.id === id) setDetail(prev => ({ ...prev, status: 'completed' }));
    } catch { notify?.('Failed to complete trip', 'error'); }
    finally { setUpdating(null); }
  };

  const doCancel = async (id) => {
    if (!window.confirm('Cancel this transport request?')) return;
    setUpdating(id);
    try {
      await api.cancelTransport(id);
      notify?.('Trip cancelled');
      load();
      if (detail?.id === id) setDetail(null);
    } catch { notify?.('Failed to cancel', 'error'); }
    finally { setUpdating(null); }
  };

  const listForTab = { pending, confirmed, completed, cancelled }[tab] || [];

  const tabs = [
    { id: 'pending',   label: `⏳ Pending (${pending.length})` },
    { id: 'confirmed', label: `🔵 Active (${confirmed.length})` },
    { id: 'completed', label: `✅ Completed` },
    { id: 'cancelled', label: `⛔ Cancelled` },
  ];

  const fmtDT = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleString('en-KE', { weekday:'short', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <PageHeader
        icon="🚗"
        title="Driver Dashboard"
        sub="Transport requests · Trip management · Dispatch"
      />

      {/* ── Summary Stats ─────────────────────────────────────── */}
      <div style={{ padding: '0 20px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: "Pending",   value: pending.length,   color: orange, icon: '⏳' },
            { label: "Active",    value: confirmed.length, color: blue,   icon: '🚗' },
            { label: "Today's Trips", value: todayTrips.length, color: gold, icon: '📅' },
            { label: "Today's Revenue", value: fmtKES(todayRevenue), color: green, icon: '💰' },
          ].map(s => (
            <div key={s.label} style={{
              background: 'var(--surface)', borderRadius: 12, padding: '16px',
              borderLeft: `3px solid ${s.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <TabBar tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {/* ── Request Cards ─────────────────────────────────────── */}
      <div style={{ padding: '0 20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--textMuted)' }}>Loading trips…</div>
        ) : listForTab.length === 0 ? (
          <Empty text={`No ${tab} transport requests`} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {listForTab.map(req => (
              <div key={req.id} style={{
                background: 'var(--surface)', borderRadius: 14, overflow: 'hidden',
                border: `1px solid rgba(255,255,255,0.06)`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }}
                onClick={() => setDetail(req)}
              >
                {/* Card header */}
                <div style={{
                  padding: '12px 16px',
                  background: `${STATUS_COLOR[req.status]}18`,
                  borderBottom: `1px solid ${STATUS_COLOR[req.status]}30`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{VEHICLE_ICON[req.vehicle_type] || '🚗'}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{req.requester_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>{req.requester_phone}</div>
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                    background: `${STATUS_COLOR[req.status]}22`,
                    color: STATUS_COLOR[req.status],
                    border: `1px solid ${STATUS_COLOR[req.status]}40`,
                  }}>
                    {STATUS_ICON[req.status]} {req.status.toUpperCase()}
                  </span>
                </div>

                {/* Route */}
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>From</div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{req.pickup_location}</div>
                    </div>
                    <span style={{ color: gold, fontSize: 18, fontWeight: 700 }}>→</span>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>To</div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{req.dropoff_location}</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                      ['📅 Pickup', fmtDT(req.pickup_datetime)],
                      ['👥 Pax', `${req.passengers} passenger${req.passengers > 1 ? 's' : ''}`],
                      ['🚗 Vehicle', (req.vehicle_type || '').replace('_', ' ')],
                      ['💰 Fee', fmtKES(req.fee)],
                    ].map(([l, v]) => (
                      <div key={l} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 7, padding: '6px 10px' }}>
                        <div style={{ fontSize: 10, color: 'var(--textMuted)' }}>{l}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {req.notes && (
                    <div style={{ fontSize: 12, color: 'var(--textMuted)', fontStyle: 'italic', marginBottom: 10, padding: '6px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                      📝 {req.notes}
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    {req.status === 'pending' && (
                      <button
                        onClick={e => { e.stopPropagation(); doConfirm(req.id); }}
                        disabled={updating === req.id}
                        style={{ ...btnPrimary, flex: 1, fontSize: 12, padding: '8px 12px' }}
                      >
                        {updating === req.id ? '…' : '✓ Accept Trip'}
                      </button>
                    )}
                    {req.status === 'confirmed' && (
                      <button
                        onClick={e => { e.stopPropagation(); doComplete(req.id); }}
                        disabled={updating === req.id}
                        style={{ ...btnPrimary, flex: 1, fontSize: 12, padding: '8px 12px', background: green }}
                      >
                        {updating === req.id ? '…' : '✅ Mark Complete'}
                      </button>
                    )}
                    {['pending', 'confirmed'].includes(req.status) && (
                      <button
                        onClick={e => { e.stopPropagation(); doCancel(req.id); }}
                        disabled={updating === req.id}
                        style={{ ...btnSm, fontSize: 12, color: red, borderColor: red + '44' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Detail Modal ─────────────────────────────────────── */}
      {detail && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setDetail(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: 16, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 32px 100px rgba(0,0,0,0.7)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{VEHICLE_ICON[detail.vehicle_type] || '🚗'}</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text)' }}>{detail.requester_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--textMuted)' }}>Transport #{detail.id}</div>
                  </div>
                </div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--textMuted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* Status badge */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <span style={{
                  fontSize: 13, fontWeight: 700, padding: '6px 18px', borderRadius: 24,
                  background: `${STATUS_COLOR[detail.status]}22`,
                  color: STATUS_COLOR[detail.status],
                  border: `1px solid ${STATUS_COLOR[detail.status]}44`,
                }}>
                  {STATUS_ICON[detail.status]} {detail.status.toUpperCase()}
                </span>
              </div>

              {/* Route visualizer */}
              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '16px', marginBottom: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{detail.pickup_location}</div>
                <div style={{ color: gold, fontSize: 22, margin: '6px 0' }}>↓</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{detail.dropoff_location}</div>
                {detail.distance_km > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 6 }}>{detail.distance_km} km</div>
                )}
              </div>

              {/* Details grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  ['Pickup Time', fmtDT(detail.pickup_datetime)],
                  ['Passengers', detail.passengers],
                  ['Vehicle', (detail.vehicle_type || '').replace('_', ' ')],
                  ['Fee', fmtKES(detail.fee)],
                  ['Payment', detail.payment_mode || '—'],
                  ['Phone', detail.requester_phone],
                ].map(([l, v]) => (
                  <div key={l} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{l}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 3 }}>{v}</div>
                  </div>
                ))}
              </div>

              {detail.notes && (
                <div style={{ padding: '10px 14px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, fontSize: 13, color: 'var(--textMuted)', marginBottom: 16 }}>
                  <span style={{ color: gold, fontWeight: 600 }}>Notes: </span>{detail.notes}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                {detail.status === 'pending' && (
                  <button onClick={() => doConfirm(detail.id)} disabled={updating === detail.id}
                    style={{ ...btnPrimary, flex: 1 }}>
                    {updating === detail.id ? '…' : '✓ Accept Trip'}
                  </button>
                )}
                {detail.status === 'confirmed' && (
                  <button onClick={() => doComplete(detail.id)} disabled={updating === detail.id}
                    style={{ ...btnPrimary, flex: 1, background: green }}>
                    {updating === detail.id ? '…' : '✅ Mark Complete'}
                  </button>
                )}
                {['pending', 'confirmed'].includes(detail.status) && (
                  <button onClick={() => doCancel(detail.id)} disabled={updating === detail.id}
                    style={{ ...btnSm, color: red }}>
                    Cancel Trip
                  </button>
                )}
                <button onClick={() => setDetail(null)} style={btnSm}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
